import React, { useEffect, useRef, useState } from "react";
import { getSchema, chatMessage } from "../API/Api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";

/**
 * Converts backend tabular data into a chat-friendly text summary
 * so NOTHING is rendered as a table under the chat.
 */
function dataToChatText(result) {
  const data = result?.data;
  if (!data?.rows?.length) return null;

  const cols = data.columns || [];
  const rows = data.rows || [];

  // Case 1: single value (like count / sum)
  if (rows.length === 1 && cols.length === 1) {
    return `النتيجة: ${rows[0]?.[cols[0]] ?? ""}`;
  }

  // Case 2: two columns -> bullet list (common group-by output)
  if (cols.length === 2) {
    const [a, b] = cols;
    const maxLines = 25;
    const lines = rows.slice(0, maxLines).map((r) => `• ${r?.[a]}: ${r?.[b]}`);
    const more =
      rows.length > maxLines ? `\n… و ${rows.length - maxLines} أخرى` : "";
    return `هذه النتائج:\n${lines.join("\n")}${more}`;
  }

  // Case 3: many columns -> preview first few rows as key:value pairs
  const maxRows = 5;
  const maxCols = 6;
  const preview = rows.slice(0, maxRows).map((r, i) => {
    const pairs = cols
      .slice(0, maxCols)
      .map((c) => `${c}: ${r?.[c] ?? ""}`);
    return `(${i + 1}) ${pairs.join(" | ")}`;
  });

  const more = rows.length > maxRows ? `\n… و ${rows.length - maxRows} صفوف أخرى` : "";
  return `تم استخراج ${rows.length} صف. معاينة:\n${preview.join("\n")}${more}`;
}

// --------------------
// Chart helpers
// --------------------
function isNumeric(v) {
  if (v === null || v === undefined) return false;
  const n = Number(v);
  return Number.isFinite(n);
}

function looksLikeTimeKey(keyName) {
  const k = (keyName || "").toLowerCase();
  return (
    k.includes("year") ||
    k.includes("yr") ||
    k.includes("السنه") ||
    k.includes("السنة") ||
    k.includes("month") ||
    k.includes("شهر") ||
    k.includes("date") ||
    k.includes("تاريخ")
  );
}

/**
 * Convert backend result -> chart spec
 * Supports: 2 columns (label, value) as object rows:
 * rows = [{colA: "x", colB: 10}, ...]
 */
function resultToChartSpec(result) {
  const data = result?.data;
  if (!data?.rows?.length) return null;

  const cols = data.columns || [];
  const rows = data.rows || [];
  if (cols.length !== 2) return null;

  const [xCol, yCol] = cols;

  // ensure y is numeric for most rows
  const numericCount = rows.filter((r) => isNumeric(r?.[yCol])).length;
  if (numericCount < Math.max(1, Math.floor(rows.length * 0.6))) return null;

  const chartData = rows.map((r) => ({
    name: String(r?.[xCol] ?? ""),
    value: Number(r?.[yCol] ?? 0),
  }));

  // choose chart type
  let type = "bar";
  if (looksLikeTimeKey(xCol)) type = "line";
  else if (chartData.length <= 6) type = "pie";

  return {
    type,
    title: result?.title || result?.question || "Visual",
    data: chartData,
    xKey: "name",
    yKey: "value",
  };
}


const PALETTE = [
  "#2563eb", "#16a34a", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#84cc16", "#f97316",
  "#14b8a6", "#a855f7", "#22c55e", "#eab308"
];

function ChartMessage({ spec }) {
  if (!spec) return null;

  return (
     <div style={{ width: "100%", minHeight: 320, direction: "ltr" }}>
      <div style={{ fontWeight: 800, marginBottom: 10, direction: "rtl" }}>
        {spec.title}
      </div>
      <div style={{ width: "100%", minWidth: 500, overflowX: "auto" }}>
        <div style={{ width: "100%", height: 50 }}>
          <ResponsiveContainer width="100%" height="100%">
            {/* chart */}
          </ResponsiveContainer>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        {spec.type === "bar" ? (
          <BarChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={spec.xKey} 
            /* interval={0}          // ✅ show every label
            angle={-35}           // ✅ rotate to fit
            textAnchor="end"
            height={50} */  />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey={spec.yKey} fill={PALETTE[0]}/>
          </BarChart>
        ) : spec.type === "line" ? (
          <LineChart data={spec.data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={spec.xKey} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey={spec.yKey} stroke={PALETTE[0]} dot={false} />
          </LineChart>
        ) : (
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie data={spec.data} dataKey={spec.yKey} nameKey={spec.xKey} outerRadius={90}> 
              {spec.data.map((_, idx) => (
                <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Sidebar example questions (like the screenshot)
 */
const EXAMPLE_GROUPS = [
  {
    title: "إحصائيات عامة",
    items: [
      "كم عدد السجلات الإجمالي في البيانات؟",
      "أظهر عدد التوصيلات حسب نوع التوصيل",
      "أظهر عدد التوصيلات حسب المنطقة",
      "أظهر عدد التوصيلات حسب المحافظة",
    ],
  },
  {
    title: "استعلامات محددة",
    items: [
      "كم عدد التوصيلات في محافظة مسقط في 2023؟",
      "أظهر عدد التوصيلات حسب الولاية",
      "أظهر عدد التوصيلات حسب نوع السكن",
    ],
  },
  {
    title: "تحليل زمني",
    items: [
      "أظهر عدد التوصيلات حسب الشهر",
      "أظهر عدد التوصيلات حسب السنة",
    ],
  },
  {
    title: "مقارنات",
    items: [
      "ما أعلى 5 ولايات في عدد التوصيلات؟",
      "قارن عدد التوصيلات بين مسقط وظفار",
    ],
  },
];

function Sidebar({ onPickQuestion, onClear }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div
      style={{
        width: 320,
        borderRight: "1px solid #e5e5e5",
        background: "#fafafa",
        height: "calc(100vh - 24px)",
        overflowY: "auto",
        borderRadius: 12,
      }}
    >
      <div style={{ padding: 14, borderBottom: "1px solid #e5e5e5" }}>
        <div style={{ fontWeight: 700 }}>Example Questions / أمثلة الأسئلة</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
          اضغط على سؤال لإرساله مباشرة
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {EXAMPLE_GROUPS.map((group, idx) => {
          const isOpen = idx === openIndex;
          return (
            <div
              key={group.title}
              style={{
                marginBottom: 10,
                border: "1px solid #e5e5e5",
                borderRadius: 12,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                style={{
                  width: "100%",
                  textAlign: "right",
                  padding: "10px 12px",
                  cursor: "pointer",
                  border: "none",
                  background: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 700,
                }}
              >
                <span>{group.title}</span>
                <span style={{ opacity: 0.7 }}>{isOpen ? "▾" : "▸"}</span>
              </button>

              {isOpen && (
                <div style={{ padding: 10, paddingTop: 0 }}>
                  {group.items.map((q) => (
                    <button
                      key={q}
                      onClick={() => onPickQuestion(q)}
                      style={{
                        width: "100%",
                        textAlign: "right",
                        padding: "10px 12px",
                        marginTop: 8,
                        borderRadius: 10,
                        border: "1px solid #e5e5e5",
                        background: "#fff",
                        cursor: "pointer",
                        lineHeight: 1.4,
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: 14 }}>
          <button
            onClick={onClear}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid #e5e5e5",
              background: "#fff",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Clear Chat / مسح المحادثة
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const [schema, setSchema] = useState([]);
  const [messages, setMessages] = useState([
    { role: "assistant", kind: "text", text: "اهلاً! اسألني عن بيانات ملف Excel 🙂" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [lastResult, setLastResult] = useState(null);
  const chatScrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getSchema();
        setSchema(s.schema || []);
      } catch (e) {
        setMessages((m) => [
          ...m,
          { role: "assistant", kind: "text", text: `Schema error: ${e.message}` },
        ]);
      }
    })();
  }, []);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  async function send(textOverride) {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", kind: "text", text }]);
    setLoading(true);

    try {
      const result = await chatMessage(text);
      setLastResult(result);

      // 1) First assistant answer: TEXT
      setMessages((m) => [
        ...m,
        { role: "assistant", kind: "text", text: result.reply || "تم." },
      ]);

      // 2) Second assistant answer: VISUAL (if possible)
      const chartSpec = resultToChartSpec(result);
      if (chartSpec) {
        setMessages((m) => [
          ...m,
          { role: "assistant", kind: "chart", chart: chartSpec },
        ]);
      } else {
        // Fallback to text summary if no chart possible
        const extra = dataToChatText(result);
        if (extra) {
          setMessages((m) => [...m, { role: "assistant", kind: "text", text: extra }]);
        }
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", kind: "text", text: `Error: ${e.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function onPickQuestion(q) {
    send(q);
  }

  function clearChat() {
    setMessages([{ role: "assistant", kind: "text", text: "تم مسح المحادثة ✅" }]);
    setInput("");
    setLastResult(null);
  }

  return (
    <div style={{ padding: 12, background: "#f3f4f6" }}>
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          display: "flex",
          gap: 12,
          flexDirection: "row-reverse",
        }}
      >
        {/* LEFT SIDEBAR */}
        <Sidebar onPickQuestion={onPickQuestion} onClear={clearChat} />

        {/* MAIN AREA */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* HEADER CARD */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              padding: 14,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontWeight: 800 }}>Excel Chatbot (Ollama)</div>
            </div>
          </div>

          {/* CHAT CARD */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 24px - 80px)",
              minHeight: 520,
              overflow: "hidden",
            }}
          >
            {/* Messages */}
            <div
              ref={chatScrollRef}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: 14,
                background: "#fff",
              }}
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  style={{
                    margin: "10px 0",
                    display: "flex",
                    justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 12px",
                      borderRadius: 12,
                      background: m.role === "user" ? "#e8f0ff" : "#f5f5f5",
                      maxWidth: m.kind === "chart" ? "95%" : "85%",
                      lineHeight: 1.5,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {m.kind === "chart" ? <ChartMessage spec={m.chart} /> : m.text}
                  </div>
                </div>
              ))}
              {loading && <div style={{ opacity: 0.7 }}>Thinking…</div>}
            </div>

            {/* Input bar */}
            <div
              style={{
                borderTop: "1px solid #eee",
                padding: 10,
                background: "#fff",
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => (e.key === "Enter" ? send() : null)}
                style={{
                  flex: 1,
                  padding: 12,
                  borderRadius: 999,
                  border: "1px solid #ddd",
                  outline: "none",
                }}
                placeholder="Ask a question about your data... / اسأل سؤالاً عن بياناتك..."
              />
              <button
                onClick={() => send()}
                style={{
                  padding: "10px 16px",
                  borderRadius: 999,
                  cursor: "pointer",
                  border: "1px solid #ddd",
                  background: "#fafafa",
                  fontWeight: 700,
                }}
              >
                ➤
              </button>
            </div>
          </div>

          {/* Debug (optional) */}
          {/* {lastResult && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: "pointer" }}>Show last response (debug)</summary>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </details>
          )} */}
        </div>
      </div>
    </div>
  );
}
